import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, getDoc, setDoc, addDoc, collection, query, where, getDocs, updateDoc, increment } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';

const FamilyIdContext = createContext();

export function useFamilyId() {
  return useContext(FamilyIdContext);
}

export function FamilyIdProvider({ children }) {
  const { currentUser, userProfile } = useAuth();
  const [familyId, setFamilyId] = useState(null);
  const [familyData, setFamilyData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadFamilyData() {
      if (!currentUser) {
        setFamilyId(null);
        setFamilyData(null);
        setLoading(false);
        return;
      }
      // currentUser exists but userProfile not yet loaded (race condition on child login)
      // Keep loading=true and wait for the next render when userProfile arrives
      if (!userProfile) {
        return;
      }

      try {
        // Check if user already has a familyId
        if (userProfile.familyId) {
          setFamilyId(userProfile.familyId);
          
          // Load family data
          const familyDoc = await getDoc(doc(db, 'families', userProfile.familyId));
          if (familyDoc.exists()) {
            setFamilyData({ id: familyDoc.id, ...familyDoc.data() });
          }
        } else {
          // User doesn't have a family yet - will need to create or join one
          setFamilyId(null);
          setFamilyData(null);
        }
      } catch (error) {
        console.error('Error loading family data:', error);
      } finally {
        setLoading(false);
      }
    }

    loadFamilyData();
  }, [currentUser, userProfile]);

  /**
   * Create a new family for the current user
   */
  const createFamily = async (familyName) => {
    if (!currentUser) throw new Error('Must be logged in to create a family');

    try {
      // Create new family document
      const familyRef = await addDoc(collection(db, 'families'), {
        name: familyName || `${userProfile?.displayName}'s Family`,
        createdAt: new Date().toISOString(),
        createdBy: currentUser.uid,
        subscriptionTier: 'trial', // Start with 14-day trial
        subscriptionStatus: 'trialing',
        trialEndsAt: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(), // 14 days
        memberCount: 1,
        inviteCode: generateInviteCode(),
        stripeCustomerId: null,
        stripeSubscriptionId: null
      });

      // Update user's familyId
      await setDoc(doc(db, 'users', currentUser.uid), {
        familyId: familyRef.id
      }, { merge: true });

      setFamilyId(familyRef.id);
      
      // Reload family data
      const familyDoc = await getDoc(familyRef);
      setFamilyData({ id: familyDoc.id, ...familyDoc.data() });

      return familyRef.id;
    } catch (error) {
      console.error('Error creating family:', error);
      throw error;
    }
  };

  /**
   * Join an existing family using an invite code
   */
  const joinFamily = async (inviteCode) => {
    if (!currentUser) throw new Error('Must be logged in to join a family');

    try {
      // Find family by invite code
      const familiesRef = collection(db, 'families');
      const q = query(familiesRef, where('inviteCode', '==', inviteCode.toUpperCase()));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        throw new Error('Invalid invite code');
      }

      const familyDoc = querySnapshot.docs[0];
      const familyId = familyDoc.id;

      // Update user's familyId
      await setDoc(doc(db, 'users', currentUser.uid), {
        familyId: familyId
      }, { merge: true });

      // Increment family member count
      await updateDoc(doc(db, 'families', familyId), {
        memberCount: increment(1)
      });

      setFamilyId(familyId);
      setFamilyData({ id: familyDoc.id, ...familyDoc.data() });

      return familyId;
    } catch (error) {
      console.error('Error joining family:', error);
      throw error;
    }
  };

  /**
   * Leave current family
   */
  const leaveFamily = async () => {
    if (!currentUser) throw new Error('Must be logged in to leave a family');
    if (!familyId) throw new Error('Not currently in a family');

    try {
      // Remove familyId from user profile
      await updateDoc(doc(db, 'users', currentUser.uid), {
        familyId: null
      });

      // Decrement family member count
      await updateDoc(doc(db, 'families', familyId), {
        memberCount: increment(-1)
      });

      setFamilyId(null);
      setFamilyData(null);

      return true;
    } catch (error) {
      console.error('Error leaving family:', error);
      throw error;
    }
  };

  const value = {
    familyId,
    familyData,
    loading,
    createFamily,
    joinFamily,
    leaveFamily,
    hasFamilyId: !!familyId
  };

  return (
    <FamilyIdContext.Provider value={value}>
      {!loading && children}
    </FamilyIdContext.Provider>
  );
}

/**
 * Generate a random 8-character invite code for enhanced security
 * 32^8 = 1.1 trillion combinations (prevents brute force)
 */
function generateInviteCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Excluding similar chars
  let code = '';
  for (let i = 0; i < 8; i++) {  // Increased from 6 to 8 for better security
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

